// Cataloghi statici dell'Asset Tracker: lookup table immutabili, bundlate nel
// client. Nessuna lettura di rete a runtime.

// ── Tipologie di asset tracciabili ──────────────────────────────────────────
// `geometry`:
//   'box'      → volume = L × W × H (tre dimensioni standard)
//   'cylinder' → volume = π · (Ø/2)² · H. Per questi asset il campo L rappresenta
//                il diametro (Ø) e W è sempre 0 (vedi computeVolumetria / form).
// `emptying: true` → l'asset si SVUOTA (contiene una risorsa che viene consumata):
// vuoto = allarme, pieno = ok → scala colori del riempimento INVERTITA.
export const ASSET_TYPES = [
  { id: 'container',       label: 'Container',       geometry: 'box',      color: '#2D9B9B' },
  { id: 'press_container', label: 'Press-container', geometry: 'box',      color: '#8b5cf6' },
  { id: 'vasca',           label: 'Vasca',           geometry: 'box',      color: '#F59E0B' },
  { id: 'cisterna',        label: 'Cisterna',        geometry: 'cylinder', color: '#3b82f6', emptying: true },
  { id: 'cassonetto',      label: 'Cassonetto',      geometry: 'box',      color: '#EC4899' },
];

// Colore associato al tipo asset (usato dai marker della mappa e dalle legende).
export const assetTypeColor = (tipoAsset) =>
  ASSET_TYPES.find(t => t.id === tipoAsset)?.color || '#6B7280';

// True se il TIPO asset si svuota di default (es. cisterna).
export const typeEmpties = (tipoAsset) =>
  !!ASSET_TYPES.find(t => t.id === tipoAsset)?.emptying;

// True se la tipologia è un cilindro (cisterna): UI con "Diametro" al posto di L
// e W forzata a 0.
export const isCylinderType = (tipoAsset) =>
  ASSET_TYPES.find(t => t.id === tipoAsset)?.geometry === 'cylinder';

// Volumetria in m³ data la tipologia e le dimensioni. Ritorna null se i valori
// necessari non sono validi.
//
//   Box       → L·W·H
//   Cilindro  (cisterna) → cilindro centrale + due bombature (calotte) alle
//     estremità, ognuna approssimata come mezzo ellissoide:
//       V = (π/6)·D²·SP_INF + (π/6)·D²·SP_SUP + π·(D/2)²·(Z_TOT − SP_INF − SP_SUP)
//     dove D = Ø (campo L), Z_TOT = altezza totale (campo H), SP_INF/SP_SUP =
//     altezza bombatura inferiore/superiore. Con SP_INF = SP_SUP = 0 la formula
//     degenera nel cilindro semplice π·(Ø/2)²·H (retrocompatibile).
export const computeVolumetria = (tipoAsset, L, W, H, spInf = 0, spSup = 0) => {
  const l = parseFloat(L), h = parseFloat(H);
  if (isCylinderType(tipoAsset)) {
    if (!(l > 0) || !(h > 0)) return null;
    const si = parseFloat(spInf) || 0;
    const ss = parseFloat(spSup) || 0;
    if (si < 0 || ss < 0) return null;
    const mid = h - si - ss;                       // altezza cilindro centrale
    if (mid < 0) return null;                       // bombature > altezza totale
    const capsVol = (Math.PI / 6) * l * l * (si + ss);
    const midVol  = Math.PI * Math.pow(l / 2, 2) * mid;
    return parseFloat((capsVol + midVol).toFixed(2));
  }
  const w = parseFloat(W);
  if (!(l > 0) || !(w > 0) || !(h > 0)) return null;
  return parseFloat((l * w * h).toFixed(2));
};

// ── Stato operativo asset ────────────────────────────────────────────────────
// In gran parte DERIVATO dalla location corrente (statusForLocationType), con
// due eccezioni:
//   - 'non_disponibile' → impostabile A MANO, solo quando l'asset è in Deposito
//   - 'in_movimento'    → automatico durante un trasferimento (per la durata
//                         del tempo stimato di transfer)
export const ASSET_STATUS = [
  { id: 'disponibile',     label: 'Disponibile',     color: '#22c55e' },
  { id: 'in_uso',          label: 'In uso',          color: '#3b82f6' },
  { id: 'in_movimento',    label: 'In movimento',    color: '#8b5cf6' },
  { id: 'manutenzione',    label: 'In manutenzione', color: '#f59e0b' },
  { id: 'non_disponibile', label: 'Non disponibile', color: '#6b7280' },
];

// Mappa tipo location → stato operativo derivato. Funzione pura (nessuna
// dipendenza), riusata da assetHelpers (display) e entityActions (persistenza).
export const statusForLocationType = (type) => ({
  Deposito:        'disponibile',
  CantiereCliente: 'in_uso',
  Officina:        'manutenzione',
}[type] || 'disponibile');

// ── Tipologie di location ────────────────────────────────────────────────────
// Tutte le location partecipano al geofencing (hanno coords + geofenceRadius).
export const LOCATION_TYPES = [
  { id: 'Deposito',        label: 'Deposito',         color: '#2D9B9B' },
  { id: 'CantiereCliente', label: 'Cantiere/Cliente', color: '#F59E0B' },
  { id: 'Officina',        label: 'Officina',         color: '#8b5cf6' },
];

// ── Tipo movimentazione ──────────────────────────────────────────────────────
// `posizione`:
//   'single' → un solo campo "Location" (carico / scarico / assegnazione)
//   'route'  → "Origine" + "Destinazione" obbligatorie (trasferimento)
export const MOVEMENT_TYPES = [
  { id: 'carico',        label: 'Carico',        posizione: 'single', color: '#22c55e' },
  { id: 'scarico',       label: 'Scarico',       posizione: 'single', color: '#ef4444' },
  { id: 'trasferimento', label: 'Trasferimento', posizione: 'route',  color: '#3b82f6' },
  { id: 'assegnazione',  label: 'Assegnazione',  posizione: 'single', color: '#8b5cf6' },
];

// Label/colore di un tipo movimentazione (fonte unica: MOVEMENT_TYPES).
export const movementLabel = (id) => MOVEMENT_TYPES.find(t => t.id === id)?.label || id;
export const movementColor = (id) => MOVEMENT_TYPES.find(t => t.id === id)?.color || '#6B7280';

// ── Unità di misura per la quantità di un asset ──────────────────────────────
// Compilabili anche contemporaneamente (es. litri + kg + m³).
export const QUANTITY_UNITS = [
  { id: 'litri', label: 'Litri', suffix: 'L'   },
  { id: 'm3',    label: 'Metri cubi', suffix: 'm³' },
  { id: 'kg',    label: 'Chilogrammi', suffix: 'kg' },
];

// ── Alert per asset ──────────────────────────────────────────────────────────
// Ogni asset può avere N regole `{ id, metric, op, threshold }`. La metrica è
// letta dall'ultima telemetria del sensore; `cap` è la capacità sensore
// richiesta (null = sempre disponibile se c'è un sensore, es. batteria).
export const ALERT_METRICS = [
  { id: 'fill',        label: 'Riempimento', suffix: '%',  cap: 'fill-level' },
  { id: 'temperature', label: 'Temperatura', suffix: '°C', cap: 'temperatura' },
  { id: 'humidity',    label: 'Umidità',     suffix: '%',  cap: 'humidity' },
  { id: 'battery',     label: 'Batteria',    suffix: '%',  cap: null },
];

export const ALERT_OPERATORS = [
  { id: 'gt', label: 'maggiore di', symbol: '>' },
  { id: 'lt', label: 'minore di',   symbol: '<' },
];

// Codici paese ISO 3166-1 alpha-2 (Italia in cima, poi UE-27 + extra-UE).
export const COUNTRY_CODES = [
  { code: 'IT', label: 'Italia'      },
  { code: 'AT', label: 'Austria'     },
  { code: 'BE', label: 'Belgio'      },
  { code: 'BG', label: 'Bulgaria'    },
  { code: 'CH', label: 'Svizzera'    },
  { code: 'CY', label: 'Cipro'       },
  { code: 'CZ', label: 'Cechia'      },
  { code: 'DE', label: 'Germania'    },
  { code: 'DK', label: 'Danimarca'   },
  { code: 'EE', label: 'Estonia'     },
  { code: 'ES', label: 'Spagna'      },
  { code: 'FI', label: 'Finlandia'   },
  { code: 'FR', label: 'Francia'     },
  { code: 'GB', label: 'Regno Unito' },
  { code: 'GR', label: 'Grecia'      },
  { code: 'HR', label: 'Croazia'     },
  { code: 'HU', label: 'Ungheria'    },
  { code: 'IE', label: 'Irlanda'     },
  { code: 'LT', label: 'Lituania'    },
  { code: 'LU', label: 'Lussemburgo' },
  { code: 'LV', label: 'Lettonia'    },
  { code: 'MT', label: 'Malta'       },
  { code: 'NL', label: 'Paesi Bassi' },
  { code: 'NO', label: 'Norvegia'    },
  { code: 'PL', label: 'Polonia'     },
  { code: 'PT', label: 'Portogallo'  },
  { code: 'RO', label: 'Romania'     },
  { code: 'SE', label: 'Svezia'      },
  { code: 'SI', label: 'Slovenia'    },
  { code: 'SK', label: 'Slovacchia'  },
  { code: 'US', label: 'Stati Uniti' },
];

// ── Catalogo sensori IoT supportati ──────────────────────────────────────────
//
// Schema:
//   id            — chiave canonica (BRAND-CODE), usata come PK
//   model         — codice prodotto vendor
//   brand         — produttore
//   tipo          — 'Livello' | 'GPS' | 'GPS Asset' | 'Ambientale'
//   technology    — per tipo 'Livello': 'Laser' | 'Ultrasound' | '3D' | 'Radar'
//   protocollo    — connettività rete
//   ipRating      — grado di protezione IP
//   batteria      — true se autonomo a batteria (include autonomiaMesi)
//   capacita      — feature: 'fill-level' | 'temperatura' | 'gps' | 'movements' |
//                   'gsm' | 'accelerometro' | 'can-bus'
//   usage         — 'asset' | 'veicolo'
//   serialPrefix  — prefisso univoco del seriale. Seriale completo =
//                   `{serialPrefix}{NNNNN}` (5 cifre, 00001-99999). Solo i modelli
//                   con serialPrefix sono assegnabili a un asset.
//
// NB: catalogo allineato 1:1 a iotilize-dashboard (src/data/catalogs.js) e
// iotilize-cloud. Non modificare qui senza aggiornare anche gli altri repo.
export const SENSOR_CATALOG = [
  // ── Fill-level (asset) ──────────────────────────────────────────────────────
  {
    id: 'IOTILIZE-LILY', model: 'LILY', brand: 'iotilize.me', tipo: 'Livello',
    technology: '3D',
    protocollo: 'LTE-M/NB-IoT', ipRating: 'IP67', batteria: true, autonomiaMesi: 60,
    capacita: ['fill-level', 'temperatura', 'gps', 'movements'],
    usage: 'asset',
    serialPrefix: 'IOT-LY-',
  },
  {
    id: 'MILESIGHT-U4', model: 'EM400-MUD', brand: 'Milesight', tipo: 'Livello',
    technology: 'Ultrasound',
    protocollo: 'LTE-M/NB-IoT', ipRating: 'IP67', batteria: true, autonomiaMesi: 60,
    capacita: ['fill-level', 'temperatura', 'gps', 'movements'],
    usage: 'asset',
    serialPrefix: 'MIL-U4-',
  },
  {
    id: 'MILESIGHT-U10', model: 'EM400-UDL', brand: 'Milesight', tipo: 'Livello',
    technology: 'Ultrasound',
    protocollo: 'LTE-M/NB-IoT', ipRating: 'IP67', batteria: true, autonomiaMesi: 60,
    capacita: ['fill-level', 'temperatura', 'gps', 'movements'],
    usage: 'asset',
    serialPrefix: 'MIL-U10-',
  },
  {
    id: 'MILESIGHT-L4', model: 'EM400-TLD', brand: 'Milesight', tipo: 'Livello',
    technology: 'Laser',
    protocollo: 'LTE-M/NB-IoT', ipRating: 'IP67', batteria: true, autonomiaMesi: 60,
    capacita: ['fill-level', 'temperatura', 'gps', 'movements'],
    usage: 'asset',
    serialPrefix: 'MIL-L4-',
  },
  {
    id: 'MILESIGHT-R12', model: 'EM410-RDL', brand: 'Milesight', tipo: 'Livello',
    technology: 'Radar',
    protocollo: 'LTE-M/NB-IoT', ipRating: 'IP67', batteria: true, autonomiaMesi: 60,
    capacita: ['fill-level'],
    usage: 'asset',
    serialPrefix: 'MIL-R12-',
  },

  // ── GPS / Telematics (veicoli) ──────────────────────────────────────────────
  {
    id: 'TELTONIKA-FMB920', model: 'FMB920', brand: 'Teltonika', tipo: 'GPS',
    protocollo: '4G LTE', ipRating: 'IP67', batteria: false,
    capacita: ['gps', 'gsm', 'accelerometro'],
    usage: 'veicolo',
  },
  {
    id: 'TELTONIKA-FMC130', model: 'FMC130', brand: 'Teltonika', tipo: 'GPS',
    protocollo: '4G LTE', ipRating: 'IP67', batteria: false,
    capacita: ['gps', 'gsm', 'can-bus'],
    usage: 'veicolo',
  },

  // ── GPS Asset tracker ────────────────────────────────────────────────────────
  {
    id: 'TELTONIKA-TAT141', model: 'TAT141', brand: 'Teltonika', tipo: 'GPS Asset',
    protocollo: 'LTE-M/NB-IoT', ipRating: 'IP67', batteria: true, autonomiaMesi: 36,
    capacita: ['gps'],
    usage: 'asset',
    serialPrefix: 'TLK-GPS-',
  },
];
